import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Banner, Spinner } from "@panelgenie/shared-ui";
import { TestCaseDetailView } from "../../components/TestCaseDetailView.js";

/**
 * TC-7 — one test case by id from `support.getTestCase`. This route owns
 * the load / error envelope, the `submitTestCaseRun` mutation, and the two
 * navigations the detail view delegates: a run's produced invocation
 * (`/invocations/$id`) and the post-submit jump to the invocation list
 * (where the new run shows as a Processing row up top). After a submit the
 * case is refetched so the run history picks up the new (pending) row.
 */
export const Route = createFileRoute("/test-cases_/$id")({
  component: TestCaseDetailPage,
});

function TestCaseDetailPage() {
  const { id } = Route.useParams();
  const { trpc } = Route.useRouteContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["test-cases", "detail", id],
    queryFn: () => trpc.support.getTestCase.query({ id }),
  });

  if (query.isError) {
    return (
      <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-5 overflow-y-auto px-4 py-6">
        <Link to="/test-cases" className="text-sm text-accent hover:underline">
          ← Back to test cases
        </Link>
        <Banner variant="danger" title="Couldn't load this test case">
          {query.error instanceof Error
            ? query.error.message
            : "Request failed. The case may not exist, or staff access is required."}
        </Banner>
      </div>
    );
  }

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center gap-3 text-mute">
        <Spinner size="md" />
        <span>Loading test case…</span>
      </div>
    );
  }

  return (
    <TestCaseDetailView
      detail={query.data}
      submitRun={async () => {
        const result = await trpc.support.submitTestCaseRun.mutate({ id });
        // Refetch so the run history shows the new (still-pending) run.
        await queryClient.invalidateQueries({
          queryKey: ["test-cases", "detail", id],
        });
        return result;
      }}
      onSelectInvocation={(invocationId) => {
        void navigate({ to: "/invocations/$id", params: { id: invocationId } });
      }}
      onViewList={() => {
        void navigate({ to: "/" });
      }}
    />
  );
}
